<script setup lang="ts">
import {
  ref,
  watchEffect,
  watch,
  computed,
  defineModel,
  onMounted,
} from 'vue'
import ALoadingMenuItem from './components/ALoadingMenuItem/index.vue'
import { useRoute, useRouter } from '#app/composables/router'

import type { Menus, Icons } from '@/types/mypage'
import type {
  GetBuyerPageOptionsResponse,
} from '@/types/api/customer/BuyerPageMeta/index'

import iconEvaluation from '@/assets/images/icon-evaluation.svg'
import iconAnalysis from '@/assets/images/icon-analysis.svg'
import iconDashboard from '@/assets/images/icon-dashboard.svg'
import iconUpload from '@/assets/images/icon-upload.svg'
import iconScorecard from '@/assets/images/icon-scorecard.svg'
import iconImprovementplans from '@/assets/images/icon-improvementplans.svg'
import iconEsgDataShare from '@/assets/images/icon/icon_esg_data_share_side_menu.svg'
import iconSettings from '@/assets/images/icon-settings.svg'
import iconTab from '@/assets/images/icon-tab.svg'
import iconLogout from '@/assets/images/icon-logout.svg'
import iconDetailExtra from '@/assets/images/icon-detail-extra.svg'
import iconQuestionGroups from '@/assets/images/icon-question-groups.svg'
import AIconClose from '@/components/atoms/AIconClose/index.vue'
import AAppLogo from '@/components/atoms/AAppLogo/index.vue'
import { UserDisplayMode } from '@/constants/enums/UserDisplayMode'
import { useAuthStore } from '@/stores/authStore'
import { trackClickLogout } from '@/utils/tracking/eventTracking'
import { getSupplierRegistTemplateMetaList } from '@/composables/api/customer/SupplierRegistTemplate'

/**
 * Props
 */
type Props = {
  menus: Menus
  isUnderBreakPoint: boolean
  buyerPageOptions?: GetBuyerPageOptionsResponse
}
const props = defineProps<Props>()

/**
 * Model
 */
const modelValue = defineModel<boolean>()

/**
 * Constants
 */
const ICONS: Icons = {
  evaluation: iconEvaluation,
  analysis: iconAnalysis,
  dashboard: iconDashboard,
  upload: iconUpload,
  scorecard: iconScorecard,
  improvementplans: iconImprovementplans,
  detailExtra: iconDetailExtra,
  questionGroups: iconQuestionGroups,
  buyerImprovementplans: iconImprovementplans,
  esgDataShare: iconEsgDataShare,
  settings: iconSettings,
  tab: iconTab,
  logout: iconLogout,
} as const
const ICON_SIZE = '20' as const

/**
 * Compositions
 */
const route = useRoute()
const router = useRouter()

/**
 * Stores
 */
const authStore = useAuthStore()

/**
 * Refs
 */
const isOpen = ref<string[]>([])
const isLoading = ref<boolean>(false)
const isDetailExtra = ref<boolean>(false)
const isEsgDataShareExpanded = ref<boolean>(false)
const isQuestionGroupsExpanded = ref<boolean>(false)

/**
 * Computed
 */
const isSettings = computed(() => {
  return route.path.includes('/settings')
})
const isUpload = computed(() => {
  return route.path.includes('/upload')
})
const isGuestBuyer = computed(() => {
  return authStore.company?.contractPlans.guestBuyer
})
const isQuestionGroupListActive = computed(() => {
  return route.path === props.menus['questionGroups']?.subPages?.list?.link
    || (route.path.startsWith(`${props.menus['questionGroups']?.subPages?.list?.link}/`)
      && !route.path.includes('/answers'))
})
const isQuestionGroupAnswersActive = computed(() => {
  return route.path === props.menus['questionGroups']?.subPages?.answers?.link
    || (route.path.startsWith(`${props.menus['questionGroups']?.subPages?.answers?.link}/`)
      && route.path.includes('/answers'))
})

/**
 * Methods
 */
const handleLogout = async () => {
  trackClickLogout()
  await router.push('/mypage/logout')
}
const isActiveItem = (menu: Menus[string]) => {
  return (
    route.path === menu.link
    || route.path.startsWith(`${menu.link}/`)
  )
}
const isDatashareActive = () => {
  return route.path.includes('/mypage/datashare')
}
const isEsgDatashareAnswerPage = (link: string) => {
  if (link === '/mypage/datashare') {
    return true
  }
  return [
    '/mypage/datashare/detail',
    '/mypage/datashare/edit',
  ].some(subPageLink => link.includes(subPageLink))
}
const isEsgDatashareSharePage = (link: string) => {
  return [
    '/mypage/datashare/company',
  ].some(subPageLink => link.includes(subPageLink))
}
const toggleEsgDataShare = () => {
  isEsgDataShareExpanded.value = !isEsgDataShareExpanded.value
}
const isQuestionGroupsActive = () => {
  return route.path.includes('/mypage/question-groups')
}
const isBuyerCreateQuestionGroupPreviewPage = () => {
  return route.name === 'mypage-question-groups-questionGroupId-preview'
}
const toggleQuestionGroups = () => {
  isQuestionGroupsExpanded.value = !isQuestionGroupsExpanded.value
}

/**
 * Lifecycle
 */
onMounted(async () => {
  const response = await getSupplierRegistTemplateMetaList({ includeIsBuyerCreated: false })
  isDetailExtra.value = response.data.value.some(item => item.hasWorkbookUrl === true)
})

watchEffect(() => {
  // ESGデータシェアのページにいる場合、展開状態にする
  if (isDatashareActive()) {
    isOpen.value = ['esgDataShare']
  }
  // アンケート作成のページにいる場合、展開状態にする
  if (isQuestionGroupsActive()) {
    isOpen.value = ['questionGroups']
  }
})

// ドロップダウンが2つ以上開かないようにする
watch(isOpen, () => {
  if (isOpen.value.length >= 2) {
    isOpen.value.splice(0, isOpen.value.length - 1)
  }
})

watch(
  () => authStore.userDisplayMode,
  () => {
    isLoading.value = true

    setTimeout(() => {
      isLoading.value = false
    }, 1000)
  },
)
</script>

<template>
  <div
    class="mypage-sidemenu"
    :class="{ 'most-front': props.isUnderBreakPoint }"
  >
    <div
      class="mypage-sidemenu-top"
      :class="{ 'icon-section': props.isUnderBreakPoint }"
    >
      <AIconClose
        v-if="props.isUnderBreakPoint"
        @click="modelValue = false"
      />
      <AAppLogo
        v-else
        color="white"
      />
    </div>
    <div class="mypage-sidemenu-content">
      <v-list
        v-model:opened="isOpen"
        density="compact"
      >
        <!-- 確認画面の場合は調査票/サーベイのみ表示（バイヤーのみ） -->
        <template
          v-if="isBuyerCreateQuestionGroupPreviewPage
            () && authStore.userDisplayMode === UserDisplayMode.BUYER"
        >
          <v-list-item
            :title="props.menus['upload'].title"
            :class="{ active: true }"
            class="confirm-survey-menu"
          >
            <atoms-base-icon
              :icon="ICONS['upload']"
              :size="ICON_SIZE"
            />
          </v-list-item>
        </template>
        <!-- 通常のメニュー表示 -->
        <template v-else>
          <ALoadingMenuItem
            v-if="isLoading"
            class="loading-menu-item"
          />
          <!-- バイヤー用ダッシュボード -->
          <v-list-item
            v-else-if="authStore.userDisplayMode === UserDisplayMode.BUYER && !isGuestBuyer"
            v-ripple
            :title="props.menus['evaluation'].title"
            :class="{ active: route.path === props.menus['evaluation'].link }"
          >
            <nuxt-link :to="props.menus['evaluation'].link" />
            <atoms-base-icon
              :icon="ICONS['dashboard']"
              :size="ICON_SIZE"
            />
          </v-list-item>
          <!-- サプライヤー用ダッシュボード -->
          <v-list-item
            v-else-if="authStore.userDisplayMode === UserDisplayMode.SUPPLIER"
            v-ripple
            :title="props.menus['dashboard'].title"
            :class="{ active: route.path === props.menus['dashboard'].link }"
          >
            <nuxt-link :to="props.menus['dashboard'].link" />
            <atoms-base-icon
              :icon="ICONS['dashboard']"
              :size="ICON_SIZE"
            />
          </v-list-item>

          <!-- 調査票/サーベイ -->
          <template v-if="authStore.userDisplayMode === UserDisplayMode.SUPPLIER">
            <ALoadingMenuItem
              v-if="isLoading"
              class="loading-menu-item"
            />
            <v-list-item
              v-ripple
              :title="props.menus['upload'].title"
              :class="{ active: isUpload }"
            >
              <nuxt-link :to="props.menus['upload'].link" />
              <atoms-base-icon
                :icon="ICONS['upload']"
                :size="ICON_SIZE"
              />
            </v-list-item>
          </template>

          <!-- スコアカード -->
          <ALoadingMenuItem
            v-if="isLoading"
            class="loading-menu-item"
          />
          <v-list-item
            v-else-if="!(authStore.userDisplayMode === UserDisplayMode.BUYER && isGuestBuyer)"
            v-ripple
            :class="{ active: isActiveItem(menus['scorecard']) }"
            :title="props.menus['scorecard'].title"
          >
            <nuxt-link :to="props.menus['scorecard'].link" />
            <atoms-base-icon
              :icon="ICONS['scorecard']"
              :size="ICON_SIZE"
            />
          </v-list-item>

          <!-- 分析 -->
          <template
            v-if="(
              authStore.userDisplayMode === UserDisplayMode.BUYER
              && !isGuestBuyer
              && buyerPageOptions && buyerPageOptions.esgSupplierRegistTemplateCount > 0
            )"
          >
            <ALoadingMenuItem
              v-if="isLoading"
              class="loading-menu-item"
            />
            <v-list-item
              v-else
              v-ripple
              :title="props.menus['analysis'].title"
              :class="{ active: route.path === props.menus['analysis'].link }"
            >
              <nuxt-link :to="props.menus['analysis'].link" />
              <atoms-base-icon
                :icon="ICONS['analysis']"
                :size="ICON_SIZE"
              />
            </v-list-item>
          </template>

          <!-- 応用分析 -->
          <template v-if="authStore.userDisplayMode === UserDisplayMode.BUYER && isDetailExtra && !isGuestBuyer">
            <ALoadingMenuItem
              v-if="isLoading"
              class="loading-menu-item"
            />
            <v-list-item
              v-else
              v-ripple
              :title="props.menus['detailExtra'].title"
              :class="{ active: route.path === props.menus['detailExtra'].link }"
            >
              <nuxt-link :to="props.menus['detailExtra'].link" />
              <atoms-base-icon
                :icon="ICONS['detailExtra']"
                :size="ICON_SIZE"
              />
            </v-list-item>
          </template>

          <ALoadingMenuItem
            v-if="isLoading"
            class="loading-menu-item"
          />
          <!-- サプライヤー用改善計画 -->
          <v-list-item
            v-else-if="authStore.userDisplayMode === UserDisplayMode.SUPPLIER"
            v-ripple
            :title="props.menus['improvementplans'].title"
            :class="{ active: isActiveItem(menus['improvementplans']) }"
          >
            <nuxt-link :to="props.menus['improvementplans'].link" />
            <atoms-base-icon
              :icon="ICONS['improvementplans']"
              :size="ICON_SIZE"
            />
          </v-list-item>
          <!-- バイヤー用改善計画 -->
          <v-list-item
            v-else-if="props.buyerPageOptions?.hasPublicImprovementPlan && !isGuestBuyer"
            v-ripple
            :title="props.menus['buyerImprovementplans'].title"
            :class="{ active: route.path === props.menus['buyerImprovementplans'].link }"
          >
            <nuxt-link :to="props.menus['buyerImprovementplans'].link" />
            <atoms-base-icon
              :icon="ICONS['buyerImprovementplans']"
              :size="ICON_SIZE"
            />
          </v-list-item>

          <ALoadingMenuItem
            v-if="isLoading"
            class="loading-menu-item"
          />
          <!-- バイヤー用ESGデータシェア -->
          <v-list-item
            v-else-if="
              authStore.userDisplayMode === UserDisplayMode.BUYER
                && props.buyerPageOptions?.hasEsgDataSharedAnswers
            "
            v-ripple
            :title="props.menus['buyerEsgDataShare'].title"
            :class="{ active: isActiveItem(props.menus['buyerEsgDataShare']) }"
          >
            <nuxt-link :to="props.menus['buyerEsgDataShare'].link" />
            <atoms-base-icon
              :icon="ICONS['esgDataShare']"
              :size="ICON_SIZE"
            />
          </v-list-item>
          <!-- サプライヤー用ESGデータシェア -->
          <v-list-group
            v-else-if="
              authStore.userDisplayMode === UserDisplayMode.SUPPLIER
                && props.menus['esgDataShare'].subPages
            "
            value="esgDataShare"
          >
            <template #activator="{ props: subPageProps }">
              <v-list-item
                v-ripple
                :min-width="100"
                v-bind="subPageProps"
                :title="props.menus['esgDataShare'].title"
                :class="{
                  'active': isDatashareActive(),
                  'item-expanded': isEsgDataShareExpanded,
                }"
                @click="toggleEsgDataShare"
              >
                <atoms-base-icon
                  :icon="ICONS['esgDataShare']"
                  :size="ICON_SIZE"
                />
                <template #append>
                  <div
                    class="arrow-icon"
                    :class="{ rotated: isEsgDataShareExpanded }"
                  >
                    <v-icon>mdi-chevron-down</v-icon>
                  </div>
                </template>
              </v-list-item>
            </template>
            <!-- 回答データ -->
            <v-list-item
              :title="props.menus['esgDataShare'].subPages.answer.title"
              :class="{ active: isEsgDatashareAnswerPage(route.path) }"
            >
              <nuxt-link :to="props.menus['esgDataShare'].subPages.answer.link" />
            </v-list-item>
            <!-- 共有データ -->
            <v-list-item
              :title="props.menus['esgDataShare'].subPages.share.title"
              :class="{ active: isEsgDatashareSharePage(route.path) }"
            >
              <nuxt-link :to="props.menus['esgDataShare'].subPages.share.link" />
            </v-list-item>
          </v-list-group>
          <!-- アンケート作成 -->
          <ALoadingMenuItem
            v-if="isLoading"
            class="loading-menu-item"
          />
          <v-list-group
            v-else-if="
              authStore.userDisplayMode === UserDisplayMode.BUYER
                && !isGuestBuyer
                && props.menus['questionGroups']?.subPages
            "
            value="questionGroups"
          >
            <template #activator="{ props: subPageProps }">
              <v-list-item
                v-ripple
                :min-width="100"
                v-bind="subPageProps"
                :title="props.menus['questionGroups'].title"
                :class="{
                  'active': isQuestionGroupsActive(),
                  'item-expanded': isQuestionGroupsExpanded,
                }"
                @click="toggleQuestionGroups"
              >
                <atoms-base-icon
                  :icon="ICONS['questionGroups']"
                  :size="ICON_SIZE"
                />
                <template #append>
                  <div
                    class="arrow-icon"
                    :class="{ rotated: isQuestionGroupsExpanded }"
                  >
                    <v-icon>mdi-chevron-down</v-icon>
                  </div>
                </template>
              </v-list-item>
            </template>
            <!-- 調査管理 -->
            <v-list-item
              :title="props.menus['questionGroups'].subPages.list.title"
              :class="{ active: isQuestionGroupListActive }"
            >
              <nuxt-link :to="props.menus['questionGroups'].subPages.list.link" />
            </v-list-item>
            <!-- 回答一覧 -->
            <v-list-item
              :title="props.menus['questionGroups'].subPages.answers.title"
              :class="{ active: isQuestionGroupAnswersActive }"
            >
              <nuxt-link :to="props.menus['questionGroups'].subPages.answers.link" />
            </v-list-item>
          </v-list-group>
        </template>
      </v-list>
    </div>
    <div class="mypage-sidemenu-bottom">
      <v-list
        v-if="!isBuyerCreateQuestionGroupPreviewPage()"
        density="compact"
      >
        <!-- 設定 -->
        <v-list-item
          v-ripple
          :title="props.menus['settings'].title"
          :class="{ active: isSettings }"
        >
          <nuxt-link :to="props.menus['settings'].link" />
          <atoms-base-icon
            :icon="ICONS['settings']"
            :size="ICON_SIZE"
          />
        </v-list-item>

        <!-- ログアウト -->
        <v-list-item
          v-ripple
          :title="props.menus['logout'].title"
        >
          <nuxt-link @click="handleLogout" />
          <atoms-base-icon
            :icon="ICONS['logout']"
            :size="ICON_SIZE"
          />
        </v-list-item>
      </v-list>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.mypage-sidemenu {
  position: fixed;
  top: 0;
  left: 0;

  // sticky-footerよりも前に持ってくる
  z-index: 2002;
  display: flex;
  flex-direction: column;
  width: $sidemenu-width;
  height: 100%;
  background: transparent
    linear-gradient(
      180deg,
      $colours-gradients-dark 0%,
      $colours-gradients-light 100%
    )
    0% 0% no-repeat padding-box;
  box-shadow: rgba($colours-black, 0.2) 0 0 4px;

  .mypage-sidemenu-top {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 23px 20px 10px;
  }

  .icon-section {
    align-items: flex-start;
  }

  .mypage-sidemenu-content,
  .mypage-sidemenu-bottom {
    padding: 16px;
  }

  .mypage-sidemenu-content {
    flex-grow: 1;
  }

  .v-list {
    color: $colours-white;
    background-color: transparent;

    .loading-menu-item {
      margin-bottom: 5px;
    }

    .v-list-item {
      margin-bottom: 5px;
      overflow: hidden;
      color: $colours-white;
      cursor: pointer;
      border-radius: 9px;

      :deep(.v-list-item__content) {
        display: flex;
        flex-direction: row-reverse;
        align-items: center;
        justify-content: flex-end;
        min-width: 130px;

        .v-list-item-title {
          overflow: visible;
          font-size: 12px;
        }

        a {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
        }

        i {
          margin-right: 10px;

          &.append {
            position: absolute;
            right: 0;
            flex: none;
            margin-top: 2px;
          }
        }
      }

      &:hover {
        background-color: rgba($colours-white, 0.25);
        transition: 0.3s;
      }

      &.active {
        background-color: $colours-blue-d;
      }

      &.confirm-survey-menu {
        cursor: default;

        &:hover {
          background-color: $colours-blue-d;
        }
      }

      .arrow-icon {
        transition: transform 0.3s ease;

        &.rotated {
          transform: rotate(-180deg);
        }
      }
    }

    .v-list-group {
      margin-bottom: 5px;
      overflow: hidden;
      border-radius: 9px;

      .v-list-item {
        margin: 0;
        border-radius: 0;

        &.item-expanded {
          background-color: rgba($colours-white, 0.2);
        }

        &:hover {
          background-color: rgba($colours-white, 0.25);
        }
      }

      .v-list-group__items {
        .v-list-item {
          padding-left: 46px !important;
          color: $colours-blue;
          background-color: $colours-white;

          &:hover {
            color: $colours-white;
            background-color: $colours-blue;
          }

          &.active {
            color: $colours-white;
            background-color: $colours-blue;
          }
        }
      }
    }
  }
}

.most-front {
  // sticky-footerよりも前に持ってくる
  z-index: 2002;
  box-shadow: 0 4px 8px rgba(var(--v-theme-ui-gray4), 0.16);
}
</style>
